import useUserQueryStore from "../context/UserStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MultiTenantConfigGetDto from "../dtos/multiTenantConfigDto";
import APIClient from "../services/apiClient";
import { toUpdateConfigPayload } from "../utils/ConfigNormalization";
import { replaceConfigInList, resolveSavedConfig } from "../utils/ConfigSaveMerge";

interface UpdateConfigContext {
  previousConfigs: MultiTenantConfigGetDto[];
}

const useUpdateConfig = (onUpdate: (updatedData: MultiTenantConfigGetDto[]) => void) => {
  const queryClient = useQueryClient();
  const currentToken = useUserQueryStore((s) => s.userQuery.currentToken);
  const apiClient = new APIClient<MultiTenantConfigGetDto>("/MultiTenantConfig", currentToken!);

  return useMutation<MultiTenantConfigGetDto, Error, MultiTenantConfigGetDto, UpdateConfigContext>({
    mutationFn: async (updatedConfig) => {
      const saved = await apiClient.put(toUpdateConfigPayload(updatedConfig) as unknown as MultiTenantConfigGetDto);
      return resolveSavedConfig(updatedConfig, saved);
    },
    onMutate: (newConfig) => {
      const previousConfigs = queryClient.getQueryData<MultiTenantConfigGetDto[]>(["configurations"]) || [];
      queryClient.setQueryData<MultiTenantConfigGetDto[]>(["configurations"], (configs) =>
        replaceConfigInList(configs, newConfig, newConfig)
      );

      return { previousConfigs };
    },
    onSuccess: (savedConfig, newConfig) => {
      queryClient.setQueryData<MultiTenantConfigGetDto[]>(["configurations"], (configs) =>
        replaceConfigInList(configs, newConfig, savedConfig)
      );
      const updatedData = queryClient.getQueryData<MultiTenantConfigGetDto[]>(["configurations"]) || [];

      onUpdate(updatedData);
    },
    onError: (_error, _newConfig, context) => {
      if (!context) return;

      queryClient.setQueryData<MultiTenantConfigGetDto[]>(["configurations"], context.previousConfigs);
    },
  });
};

export default useUpdateConfig;
